<template>
  <dd-header-page
      :header="title"
      :bread-crumbs="automationCrumbs"
  >
    <template #header-buttons>
      <div class="flex-col align-items-end gap-md mb-sm ml-auto">
        <dd-list-button
            @click="showResultsConfirmModal = true"
            :count="hasUnprocessedResults ? 1 : 0"
        >
          Handmatige controles
          <template #icon>
            <CheckboxMultipleOutlineIcon/>
          </template>
        </dd-list-button>
        <AutomationResultConfirmModal
            v-if="automationInterface"
            :automations="[automationInterface]"
            :visible="showResultsConfirmModal"
            @close="showResultsConfirmModal = false"
        />
      </div>
    </template>
    <template #content>
      <div class="flex-row gap-md">
        <dd-sub-tab-group orientation="vertical" class="mb-md">
          <dd-sub-tab>
            Algemeen
          </dd-sub-tab>
          <dd-sub-tab v-show="sendsEmail">
            E-mail instellingen
          </dd-sub-tab>
          <dd-sub-tab v-show="createsTask">
            Taak instellingen
          </dd-sub-tab>
          <dd-sub-tab v-show="createsSignal">
            Signaal instellingen
          </dd-sub-tab>
          <dd-sub-tab>
            Geschiedenis
          </dd-sub-tab>
          <dd-sub-tab>
            Selectie
          </dd-sub-tab>
        </dd-sub-tab-group>
        <dd-sub-tab-content-wrapper :style="{width: '100%' }">
          <dd-sub-tab-content>
            <dd-customer-page-container>
              <ShowAutomationComponent v-if="automationInterface" :automation="automationInterface"
                                       :is-global="isGlobal"/>
              <dd-loading-indicator v-else-if="!loadedAutomationInterface"></dd-loading-indicator>
            </dd-customer-page-container>
          </dd-sub-tab-content>

          <dd-sub-tab-content v-show="sendsEmail">
            <dd-customer-page-container>
              <show-email-settings-component v-if="automationInterface" v-bind:value="automationInterface"/>
            </dd-customer-page-container>
          </dd-sub-tab-content>

          <dd-sub-tab-content v-show="createsTask">
            <dd-customer-page-container>
              <show-task-settings-component v-if="automationInterface" v-bind:value="automationInterface"/>
            </dd-customer-page-container>
          </dd-sub-tab-content>

          <dd-sub-tab-content v-show="createsSignal">
            <dd-customer-page-container>
              <show-signal-settings-component v-if="automationInterface" v-bind:value="automationInterface"/>
            </dd-customer-page-container>
          </dd-sub-tab-content>
          <dd-sub-tab-content>
            <dd-customer-page-container>
              <AutomationResultsHistory/>
            </dd-customer-page-container>
          </dd-sub-tab-content>
          <dd-sub-tab-content>
            <dd-customer-page-container>
              <AutomationSearchOverview/>
            </dd-customer-page-container>
          </dd-sub-tab-content>

        </dd-sub-tab-content-wrapper>
      </div>
    </template>
    <template #buttons>
      <automation-execution-switch
          v-if="automationInterface"
          @saved="fetchConfiguredAutomationInterface"
          :automation="automationInterface">
        <template #button="{ isActive }">
          <dd-rounded-button
              v-if="isActive"
              data-dusk="disable-global-automation-button"
              type="danger"
          >
            <template #icon>
              <PowerIcon/>
            </template>
            Automation uitzetten
          </dd-rounded-button>
          <dd-rounded-button
              v-else
              data-dusk="enable-global-automation-button"
              type="success"
          >
            <template #icon>
              <PowerIcon/>
            </template>
            Automation aanzetten
          </dd-rounded-button>
        </template>
      </automation-execution-switch>
      <dd-confirm-modal
          title="Je staat op het punt een automation uit te voeren"
          description="Weet je zeker dat je deze automation uit wilt voeren?"
          @confirm="onExecuteAutomation"
      >
        <dd-rounded-button
            data-dusk="start-automation-button"
        >
          <template #icon>
            <PlayIcon/>
          </template>
          Automation uitvoeren
        </dd-rounded-button>
      </dd-confirm-modal>
      <dd-rounded-button @click="editAutomation">
        <template #icon>
          <PencilIcon/>
        </template>
        Automation bewerken
      </dd-rounded-button>
      <dd-confirm-modal
          title="Je staat op het punt een automation te verwijderen"
          description="Weet je zeker dat je deze automation wilt verwijderen? Deze actie is onomkeerbaar."
          @confirm="deleteAutomation"
      >
        <dd-rounded-button
            data-dusk="delete-automation-button"
            type="danger"
        >
          <template #icon>
            <TrashIcon/>
          </template>
          Automation verwijderen
        </dd-rounded-button>
      </dd-confirm-modal>
    </template>
  </dd-header-page>
</template>

<script>
import store from '~/pages/automation/components/show/store'
import automationApi from '~/api/automation'
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex'

import AutomationableTypeEnum from '~/pages/automation/enums/AutomationableTypeEnum'
import AutomationResultsHistory from '~/pages/automation/components/show/AutomationResultsHistory.vue'
import AutomationSearchOverview from '~/pages/automation/components/show/AutomationSearchOverview.vue'
import ShowAutomationComponent from '~/pages/automation/components/show/ShowAutomationComponent.vue'
import ShowTaskSettingsComponent from '~/pages/automation/components/show/ShowTaskSettingsComponent.vue'
import ShowEmailSettingsComponent from '~/pages/automation/components/show/ShowEmailSettingsComponent.vue'
import ShowSignalSettingsComponent from '~/pages/automation/components/show/ShowSignalSettingsComponent.vue'
import SelectionPreviewComponent from '~/pages/automation/components/SelectionPreviewComponent.vue'
import AutomationResultConfirmModal from '~/pages/automation/components/result/AutomationResultConfirmModal.vue'
import AutomationExecutionSwitch from '~/pages/automation/components/AutomationExecutionSwitch.vue'
import globalAutomationConfigurationApi from '~/api/global_automation_configuration'

export default {
  name: 'ShowScreen',
  components: {
    AutomationExecutionSwitch,
    AutomationResultsHistory,
    AutomationResultConfirmModal,
    SelectionPreviewComponent,
    ShowSignalSettingsComponent,
    ShowTaskSettingsComponent,
    ShowEmailSettingsComponent,
    ShowAutomationComponent,
    AutomationSearchOverview,
  },
  props: {
    isGlobal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      automationCrumbs: [
        {
          name: 'Automations',
          to: () => this.navigator.navigatePath(this.route('vue.dashboard.automations.index'))
        }
      ],
      showResultsConfirmModal: false,
    }
  },
  beforeCreate() {
    const STORE_KEY = '$_automation_show'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
  },
  activated() {
    this.setAutomationInterfaceId(this.automationId)
    this.setIsGlobal(this.isGlobal)

    this.fetchConfiguredAutomationInterface({
      id: this.automationId,
      type: this.isGlobal ?
          AutomationableTypeEnum.GLOBAL_AUTOMATION :
          AutomationableTypeEnum.AUTOMATION,
    })
  },
  computed: {
    ...mapGetters('$_automation_show', {
      automationInterface: 'automationInterface',
      sendsEmail: 'sendsEmail',
      createsTask: 'createsTask',
      createsSignal: 'createsSignal',
      isActive: 'isActive',
      title: 'title',
      hasUnprocessedResults: 'hasUnprocessedResults',
    }),
    ...mapState('$_automation_show', {
      loadedAutomationInterface: 'loadedAutomationInterface',
    }),
    automationId() {
      return this.$route.params.automation
    },
  },
  methods: {
    ...mapActions('$_automation', {
      executeAutomation: 'executeAutomation',
      disableAutomation: 'disableAutomation',
      updateAutomation: 'updateAutomation',
      selectAutomation: 'selectAutomation',
      // global automation
      selectGlobalAutomation: 'selectGlobalAutomation',
      executeOneTimeDataEnrichment: 'executeOneTimeDataEnrichmentGlobalAutomationConfiguration',
      executeGlobalAutomationConfiguration: 'executeGlobalAutomationConfiguration',
      disableGlobalAutomationConfiguration: 'disableGlobalAutomationConfiguration',
      selectGlobalAutomationConfiguration: 'selectGlobalAutomationConfiguration',
    }),
    ...mapMutations('$_automation', {
      setCreatingAutomation: 'SET_CREATING_AUTOMATION',
      resetCreatingAutomation: 'RESET_CREATING_AUTOMATION_TO_DEFAULT',
    }),
    ...mapActions('$_automation_show', {
      fetchConfiguredAutomationInterface: 'fetchConfiguredAutomationInterface',
    }),
    ...mapMutations('$_automation_show', {
      setAutomationInterfaceId: 'SET_AUTOMATION_INTERFACE_ID',
      setIsGlobal: 'SET_IS_GLOBAL',
    }),
    editAutomation() {
      if (this.isGlobal) {
        this.navigator.navigatePath(this.route('vue.dashboard.automations.global.edit', {automation: this.automationId}))
      } else {
        this.navigator.navigatePath(this.route('vue.dashboard.automations.edit', {automation: this.automationId}))
      }
    },
    onExecuteAutomation() {
      let promise
      if (this.isGlobal) {
        promise = globalAutomationConfigurationApi.run(this.automationInterface.id)
      } else {
        promise = automationApi.run(this.automationInterface.id)
      }
      promise.then(() => {
        this.$notification.success({
          message: 'Automation gestart',
          description: 'De automation is gestart en wordt op de achtergrond uitgevoerd'
        })
      })
          .catch((error) => {
            this.$notification.error({
              message: 'Er ging iets mis',
              description: error.message
            })
          })
    },
    deleteAutomation() {
      let promise
      if (this.isGlobal) {
        promise = globalAutomationConfigurationApi.destroy(this.automationInterface.id)
      } else {
        promise = automationApi.destroy(this.automationInterface.id)
      }
      promise.then(() => {
        this.navigator.navigatePath(this.route('vue.dashboard.automations.index'))
      })
          .catch((error) => {
            this.$notification.error({
              message: 'Er ging iets mis',
              description: error.message
            })
          })
    },
    notify(response) {
      this.$notification[response.success ? 'success' : 'error']({
        message: response.success ? 'Campagne is succesvol opgeslagen' : 'Er ging iets mis',
        description: response.success ? undefined : response.message
      })
    },
  },
}
</script>

<style scoped>

</style>
